<script setup lang="ts">
import { ref, computed } from '#imports';
import { vOnClickOutside } from '@vueuse/components';
import { watchDebounced } from '@vueuse/core';

import type { ItemList } from '@/shared/types';
import mapperSelect from '@/shared/utils/mapper-select';

import LoaderSpinner from '@/shared/components/loader/loader-spinner.vue';
import IconSearch from '@/assets/icons/icon-search.svg';
import IconAngle from '@/assets/icons/icon-angle.svg';

type InputSearchVariation = 'absolute' | 'outline';

const props = withDefaults(defineProps<{
	value?: string | number;
	label?: string;
	placeholder?: string;
	hasFocus?: boolean,
	isHeader?: boolean;
	variations?: Array<InputSearchVariation>;
	mapperOptions?: [string, string];
	fetchFunc?: (args?: any) => Promise<any>;
}>(), {
	value: '',
	label: '',
	placeholder: '',
	hasFocus: false,
	list: () => [],
	variations: () => [],
	mapperOptions: () => ['id', 'value'],
	fetchFunc: async () => {},
});

const emit = defineEmits<{
	(e: 'pick', findValue: ItemList): void;
	(e: 'reset-value'): void;
	(e: 'on-navigate'): void;
}>();

const isFocus = ref(props.hasFocus);
const handleHideList = () => {
	isFocus.value = false;
	emit('on-navigate');
};

const isInput = ref(false);
const handleFieldInput = () => {
	isFocus.value = true;

	if (search.value.length > 2) {
		isInput.value = true;
	}
};

const search = ref('');
const list = ref([] as Array<ItemList>);
const isLoading = ref(false);

const isComputedLoading = computed(() => isLoading.value || isInput.value);
const computedResultText = computed(() => isComputedLoading.value ? '<b>...</b>' : 'Ничего не найдено');

const field = ref<null | { input: HTMLInputElement | HTMLTextAreaElement }>(null);

const computedClass = computed(() => {
	const modificators = props.variations?.map((variation) => {
		return `search--${variation}`;
	});

	const classes = {
		'search--focus': isFocus.value,
	};

	return ['search', ...modificators, classes];
});

watchDebounced(search, async (val: string) => {
	if (val === '' || val.length <= 2) {
		isLoading.value = false;
		isInput.value = false;
		list.value = [];
		emit('reset-value');

		return;
	}

	isLoading.value = true;

	const data = await props.fetchFunc({
		startswith: search.value.toLowerCase(),
	});

	if ('error' in data) {
		list.value = [];
	}

	list.value = mapperSelect(data.results, props.mapperOptions);

	isLoading.value = false;
	isInput.value = false;
}, { debounce: 1000 });

const handlePick = (item: ItemList) => {
	search.value = item.value;
	props.isHeader && handleHideList();
	emit('pick', item);

	isFocus.value = false;
};

// TODO: @blur
// const handleBlur = useDebounceFn(handleHideList, 0);

defineExpose({ field });
</script>

<template>
	<div v-on-click-outside="handleHideList" :class="computedClass">
		<div class="search__field">
			<v-input
				ref="field"
				v-model="search"
				v-bind="$attrs"
				:label
				:placeholder
				type="search"
				has-action
				@focus="isFocus = true"
				@click="isFocus = true"
				@input="handleFieldInput"
				@keydown.esc="handleHideList"
			>
				<template #action>
					<loader-spinner v-if="isComputedLoading" class="gray" />
					<icon-search v-else class="icon" />
				</template>
			</v-input>
		</div>

		<div v-if="isFocus && search.length > 2" class="search__dropdown">
			<v-scroll
				v-if="list && list.length > 0"
				tag="ul"
				class="search__list"
			>
				<li
					v-for="item in list"
					:key="item.id"
					class="search__list-item"
				>
					<button
						type="button"
						class="search__list-button button"
						@click="handlePick(item)"
						v-text="item.value"
					/>
				</li>
			</v-scroll>

			<p v-else class="search__list-result" v-html="computedResultText" />

			<div v-if="isHeader" class="search__footer">
				<nuxt-link to="/search" class="search__link" @click="handleHideList">
					<icon-angle class="icon icon--angle-left icon--mini search__link-icon" />
					Перейти к результатам
				</nuxt-link>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.search {
	position: relative;

	border-radius: rem($radius-secondary);

	&--outline {
		padding: rem(12px) rem($gap-small);

		@media #{$screen-laptop} {
			padding: rem(14px) rem($gap-small);
		}
	}

	&--outline#{&}--focus {
		max-height: rem(320px);

		border: rem(1px) solid $black;
	}

	&--absolute & {
		&__dropdown {
			position: absolute;
			z-index: $z-index-dropdown;
			top: 0;
			right: 0;
			left: 0;

			padding-left: rem($gap-mini);

			transform: translateY(rem(28px));

			border: rem(1px) solid darken($wild-sand, 5%);
			border-radius: rem($radius-secondary);

			@media #{$screen-tablet} {
				padding-left: rem(12px);
			}
		}

		&__list-item:first-child {
			margin-top: rem($gap-mini);

			@media #{$screen-tablet} {
				margin-top: rem(12px);
			}
		}

		&__list-item:last-of-type {
			margin-bottom: rem($gap-mini);

			@media #{$screen-tablet} {
				margin-bottom: rem(12px);
			}
		}

		&__list-footer {
			padding-bottom: 0;
		}
	}

	&__dropdown {
		margin-top: rem(12px);

		background-color: $white;
	}

	&__list {
		overflow-y: auto;

		max-height: rem(240px);

		&-button {
			@include text-small(500);

			width: 100%;
			max-height: rem(210px);
			padding: rem($gap-mini) 0;

			transition: color $transition-duration $transition-function;
			text-align: left;
			cursor: pointer;

			color: $black;

			@include hover {
				color: $dusty-gray;
			}
		}

		&-result {
			@include text-small;

			margin: rem(12px) 0;
		}
	}

	&__footer {
		padding: rem($gap-small) 0 rem($gap-mini);

		border-top: rem(1px) solid #71717a;
	}

	&__link {
		@include text-small(600);

		display: inline-flex;
		align-items: center;
		justify-content: center;

		text-decoration: none;

		color: $black;

		&-icon {
			margin-right: rem($gap-micro);
		}
	}
}
</style>
